import HttpService from './HttpService';

const API_URL = '/rooms';

/**
 * Format error for consistent handling across components
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} - Structured error with message and code
 */
const formatError = (error, defaultMessage) => {
  const data = error.response?.data;
  // The rooms backend puts its human-readable string under `error`
  // (api_response) — reading only `message` made every server message
  // ("Name is required", "Room already has the maximum of 10 images", …)
  // collapse into the generic default. Field-level Pydantic errors arrive
  // as an `errors` list; expose it so forms can render per-field.
  const errorMessage = data?.message || data?.error || defaultMessage;
  const errorCode = error.response?.status;
  const details = data?.details || {};
  const errors = Array.isArray(data?.errors) ? data.errors : [];

  // Only log errors in development to avoid exposing sensitive info in production
  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return {
    message: errorMessage,
    code: errorCode,
    originalError: error,
    details,
    errors,
    error_code: data?.error_code,
  };
};

const RoomService = {
  /**
   * Get rooms for the current user with pagination, search, and sort.
   * @param {Object} [params]
   * @param {number} [params.limit] - Max rooms (default 20, max 100 server-side)
   * @param {number} [params.offset] - Pagination offset
   * @param {string} [params.search] - Case-insensitive partial match on name/floor
   * @param {'recently_updated'|'name'|'oldest'} [params.sort]
   * @param {number} [params.house_id] - Filter to rooms anchored to this house.
   *   Rejects a non-integer with 400 and a house the user doesn't own with 404
   *   (never an empty list — that would be indistinguishable from an empty house).
   * @returns {Promise<{rooms: Array, pagination: {total, limit, offset, has_more}}>}
   */
  getRooms: async (params = {}) => {
    try {
      const response = await HttpService.get(API_URL, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching rooms');
    }
  },

  /**
   * Create a new room for the current user
   * @param {Object} data - Room data (name, floor, room_type, properties, ...)
   * @returns {Promise<Object>} - Created room
   */
  createRoom: async (data) => {
    try {
      const response = await HttpService.post(API_URL, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating room');
    }
  },

  /**
   * Get a specific room by ID (Metadata only)
   * @param {number} id - Room ID
   * @returns {Promise<Object>} - Room details (no items or tasks lists)
   */
  getRoom: async (id) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching room ${id}`);
    }
  },

  /**
   * Update a room
   * @param {number} id - Room ID
   * @param {Object} data - Room data to update
   * @returns {Promise<Object>} - Updated room
   */
  updateRoom: async (id, data) => {
    try {
      const response = await HttpService.put(`${API_URL}/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating room ${id}`);
    }
  },

  /**
   * Delete a room
   * @param {number} id - Room ID
   * @param {Object} options - Deletion options (delete_items, delete_tasks)
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteRoom: async (id, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.delete_items) params.append('delete_items', 'true');
      if (options.delete_tasks) params.append('delete_tasks', 'true');

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await HttpService.delete(`${API_URL}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting room ${id}`);
    }
  },

  /**
   * Get items for a room with pagination
   * @param {number} id - Room ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Maximum number of items to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} - List of items and pagination metadata
   */
  getRoomItems: async (id, params = {}) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}/items`, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching items for room ${id}`);
    }
  },

  /**
   * Get tasks for a room with pagination
   * @param {number} id - Room ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Maximum number of tasks to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} - List of tasks and pagination metadata
   */
  getRoomTasks: async (id, params = {}) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}/tasks`, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching tasks for room ${id}`);
    }
  },

  /**
   * Get the latest extracted scan for a specific room
   * @param {number} id - Room ID
   * @returns {Promise<Object>} - Scan data
   */
  getRoomScan: async (id) => {
    try {
      const response = await HttpService.get(`/room-scans/room/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching scan for room ${id}`);
    }
  },

  /**
   * Get all floor details for a room
   */
  getRoomFloorDetails: async (roomId) => {
    try {
      const response = await HttpService.get(`${API_URL}/${roomId}/floor-details`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching floor details for room ${roomId}`);
    }
  },

  createRoomFloorDetail: async (roomId, data) => {
    try {
      const response = await HttpService.post(`${API_URL}/${roomId}/floor-details`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error creating floor detail for room ${roomId}`);
    }
  },

  updateRoomFloorDetail: async (roomId, floorDetailId, data) => {
    try {
      const response = await HttpService.put(`${API_URL}/${roomId}/floor-details/${floorDetailId}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating floor detail ${floorDetailId}`);
    }
  },

  deleteRoomFloorDetail: async (roomId, floorDetailId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${roomId}/floor-details/${floorDetailId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting floor detail ${floorDetailId}`);
    }
  },

  /**
   * Upload an image for a room (stored in S3/R2)
   * @param {number} roomId - Room ID
   * @param {File} file - Image file
   * @param {boolean} isPrimary - Whether to set as primary image
   * @returns {Promise<Object>} - Uploaded image data with presigned URL
   */
  uploadRoomImage: async (roomId, file, isPrimary = false) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (isPrimary) formData.append('is_primary', 'true');
      const response = await HttpService.post(`${API_URL}/${roomId}/images`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error uploading room image');
    }
  },

  /**
   * Delete an image from a room
   * @param {number} roomId - Room ID
   * @param {number} imageId - Image ID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteRoomImage: async (roomId, imageId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${roomId}/images/${imageId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error deleting room image');
    }
  },

  /**
   * Set an image as the primary image for a room
   * @param {number} roomId - Room ID
   * @param {number} imageId - Image ID
   * @returns {Promise<Object>} - Updated image data
   */
  setRoomImagePrimary: async (roomId, imageId) => {
    try {
      const response = await HttpService.put(`${API_URL}/${roomId}/images/${imageId}/primary`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error setting primary image');
    }
  },

  /**
   * Get configuration options for rooms
   * @returns {Promise<Object>} - Room configuration arrays
   */
  getConfig: async () => {
    try {
      const response = await HttpService.get(`${API_URL}/config`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching room configuration');
    }
  }
};

export default RoomService;
